import * as THREE from 'three'
import Materials from './Materials.js'
import Floor from './Floor.js'
import Shadows from './Shadows.js'
import Physics from './Physics.js'
import Zones from './Zones.js'
import Objects from './Objects.js'
import Car from './Car.js'
import Areas from './Areas.js'
import Tiles from './Tiles.js'
import Walls from './Walls.js'
import IntroSection from './Sections/IntroSection.js'
import ProjectsSection from './Sections/ProjectsSection.js'
import CrossroadsSection from './Sections/CrossroadsSection.js'
import InformationSection from './Sections/InformationSection.js'
import PlaygroundSection from './Sections/PlaygroundSection.js'
// import DistinctionASection from './Sections/DistinctionASection.js'
// import DistinctionBSection from './Sections/DistinctionBSection.js'
// import DistinctionCSection from './Sections/DistinctionCSection.js'
// import DistinctionDSection from './Sections/DistinctionDSection.js'
import Controls from './Controls.js'
import Sounds from './Sounds.js'
import { TweenLite } from 'gsap/TweenLite'
import { Power2 } from 'gsap/EasePack'
import EasterEggs from './EasterEggs.js'

export default class {
    constructor(_options) {
        // Options
        this.config = _options.config
        this.debug = _options.debug
        this.resources = _options.resources
        this.time = _options.time
        this.sizes = _options.sizes
        this.camera = _options.camera
        this.renderer = _options.renderer
        this.passes = _options.passes

        // Debug
        if (this.debug) {
            this.debugFolder = this.debug.addFolder('world')
            this.debugFolder.open()
        }

        // Set up
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        // this.setAxes()
        this.setSounds()
        this.setControls()
        this.setFloor()
        this.setAreas()
        this.setStartingScreen()
    }

    start() {
        window.setTimeout(() => {
            this.camera.pan.enable()
        }, 2000)

        this.setReveal()
        this.setMaterials()
        this.setShadows()
        this.setPhysics()
        this.setZones()
        this.setObjects()
        this.setCar()
        this.areas.car = this.car
        this.setTiles()
        this.setWalls()
        this.setSections()
        this.setEasterEggs()
    }

    setReveal() {
        this.reveal = {}
        this.reveal.matcapsProgress = 0
        this.reveal.floorShadowsProgress = 0
        this.reveal.previousMatcapsProgress = null
        this.reveal.previousFloorShadowsProgress = null

        // Go method
        this.reveal.go = () => {
            TweenLite.fromTo(this.reveal, 3, { matcapsProgress: 0 }, { matcapsProgress: 1 })
            TweenLite.fromTo(this.reveal, 3, { floorShadowsProgress: 0 }, { floorShadowsProgress: 1, delay: 0.5 })
            TweenLite.fromTo(this.shadows, 3, { alpha: 0 }, { alpha: 0.5, delay: 0.5 })

            if (this.sections.intro) {
                TweenLite.fromTo(this.sections.intro.instructions.arrows.label.material, 0.3, { opacity: 0 }, { opacity: 1, delay: 0.5 })
                if (this.sections.intro.otherInstructions) {
                    TweenLite.fromTo(this.sections.intro.otherInstructions.label.material, 0.3, { opacity: 0 }, { opacity: 1, delay: 0.75 })
                }
            }

            // Car
            this.physics.car.chassis.body.sleep()
            this.physics.car.chassis.body.position.set(0, 0, 12)

            window.setTimeout(() => {
                this.physics.car.chassis.body.wakeUp()
            }, 300)

            // Sound
            TweenLite.fromTo(this.sounds.engine.volume, 0.5, { master: 0 }, { master: 0.7, delay: 0.3, ease: Power2.easeIn })
            window.setTimeout(() => {
                this.sounds.play('reveal')
            }, 400)

            // Controls
            if (this.controls.touch) {
                window.setTimeout(() => {
                    this.controls.touch.reveal()
                }, 400)
            }
        }

        // Time tick
        this.time.on('tick', () => {
            // Matcap progress changed
            if (this.reveal.matcapsProgress !== this.reveal.previousMatcapsProgress) {
                // Update each material
                for (const _materialKey in this.materials.shades.items) {
                    const material = this.materials.shades.items[_materialKey]
                    material.uniforms.uRevealProgress.value = this.reveal.matcapsProgress
                }

                // Save
                this.reveal.previousMatcapsProgress = this.reveal.matcapsProgress
            }

            // Matcap progress changed
            if (this.reveal.floorShadowsProgress !== this.reveal.previousFloorShadowsProgress) {
                // Update each floor shadow
                for (const _mesh of this.objects.floorShadows) {
                    _mesh.material.uniforms.uAlpha.value = this.reveal.floorShadowsProgress
                }

                // Save
                this.reveal.previousFloorShadowsProgress = this.reveal.floorShadowsProgress
            }
        })

        // Debug
        if (this.debug) {
            this.debugFolder.add(this.reveal, 'matcapsProgress').step(0.0001).min(0).max(1).name('matcapsProgress')
            this.debugFolder.add(this.reveal, 'floorShadowsProgress').step(0.0001).min(0).max(1).name('floorShadowsProgress')
            this.debugFolder.add(this.reveal, 'go').name('reveal')
        }
    }

    setStartingScreen() {
        this.startingScreen = {}

        // Area
        this.startingScreen.area = this.areas.add({
            position: new THREE.Vector2(0, 0),
            halfExtents: new THREE.Vector2(2.35, 1.5),
            hasKey: false,
            testCar: false,
            active: false
        })

        // Loading label
        this.startingScreen.loadingLabel = {}
        this.startingScreen.loadingLabel.geometry = new THREE.PlaneBufferGeometry(2.5, 2.5 / 4)
        this.startingScreen.loadingLabel.image = new Image()
        this.startingScreen.loadingLabel.image.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAABABAMAAAAHc7SNAAAAMFBMVEUAAAD///9ra2ucnJzR0dH09PQmJiaNjY24uLjp6end3d1CQkLFxcVYWFiqqqp9fX3nQ5qrAAAEVUlEQVRo3u3YT08TQRQA8JEtW6CATGnDdvljaTwYE2IBI/HGRrwSetGTsZh4MPFQYiQe229gE++WePFY9Oqh1cRzieEDYIgXLxjPJu5M33vbZQszW+fgoS+B7ewO836znRl2lg1jGMP4P2Okw0yFvaKsklr3I99Tvl3iPPelGbQhKqxB4eN6N/7gVcsvbEAz1F4RLn67zzl/v6/oLvejGBQ9LsNphio4UFjmEAsVJuOK/zkDtc6w+gyTcZ3LyP6IAzjBDA+pj6LkEgAjW4kANsMAC6vmOvqAMU5RgVOTskQACicCmCcA9AXjkT5gj1MswqlxWcoTgKJ6HuAQAD5guNoAu8QpMnBul1ONMGD2PCBbRgDAKYq6AEtmXvtdj3S6GhRyW1t1DvkAgM0ggG7mu1t3xWFHFzAqv3wYCi0mY1UCGgiQPU+1oWIY8LoXcAA3qeYfr+kClvHW14PJ5OfCAgHYNAoDAORBQIrDvHjqH5c0ANTbORzBacbAQgUC2IAKAzI9gCSHlWEMLmgBPJxMvyARpIICALDm4nkAbwIA71EZx5UOgO48JnLoOhQIAN9sOgKoBoAE5r0aB8ARcNhtFzrg0VQmwCp8CAMeAADGc44S5GMBsF1aCEU2LcAcAPDCvwFytBDehCaUgJxRAKeF8BNUUQJ43iiAUlqwFKoBrTCAHjiagwEgU0YM5IYWYD4KoIgPwIXQwUbVgCXzgLpIBJNeDciWTQNskVsq1ADX/6kYBdCTjse5owbMiX+IpgGWOCPSuWpA2vN/TAMm5QTYg5IC4FdbMA0YF5Nb5s2rAaLyhzBgektGZWDArrgqi0U1QHxf38OABDwUDgTAjGfyPlTVgJT/67FBACbqyGYaaoBctQwD2vI4DecVAPkgZRhQlxPQks2rAePGAbZsRlaa1QBYEQBUHRCAmaXD0QDYxgFWdye05R9cDQCrmQYkeBA6gGXTgNEeQF4DMG4S4MLjOUZRA5A0CcjADgmjqgGwSwSg9wK1GIBS74KTgTxv/EHoiaVQsTOS5RoCJuiZyosB8EIrHpyowFiYofO0i4wCjhCQwL0hq2sCaFNM22S4JXloLk0AuLDTBzCBAAt3xykeA7CHe/mDbgdTvQ9GswSAwdbqA0giYASHjQUJnhQKhQ6z/d8rDA4hAG2Dsk042ejubHMM2nV6AMf93pCkaRjhh0WsWuz+6aasl2FwiAImReEts1/CSaFfwFouAJxC4RW+I4oCThBQE1X2WbKkBFDkqYDtJ0SHaYKq3pJJwCECjjiFPoC1w+2P0gumurgeBjT6AhIIGKOelGIAngWlFnRnMZjMIYBb7gtIIsAuYU+8GICpEhYyZVgIZ2g9rYYAX1lfAKvjnxzjnWrHALDn9K1h2k2aoI1ewGd2AWAVAVMHcKdW4wDYje739pNufJXhkJohgLu9zy4CHCKAJYUge4ddCojGyPrp9kaHmYjUi9N7+2wYwxjGZfEXMKxGE0GkkfIAAAAASUVORK5CYII='
        this.startingScreen.loadingLabel.texture = new THREE.Texture(this.startingScreen.loadingLabel.image)
        this.startingScreen.loadingLabel.texture.magFilter = THREE.NearestFilter
        this.startingScreen.loadingLabel.texture.minFilter = THREE.LinearFilter
        this.startingScreen.loadingLabel.texture.needsUpdate = true
        this.startingScreen.loadingLabel.material = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.startingScreen.loadingLabel.texture })
        this.startingScreen.loadingLabel.mesh = new THREE.Mesh(this.startingScreen.loadingLabel.geometry, this.startingScreen.loadingLabel.material)
        this.startingScreen.loadingLabel.mesh.matrixAutoUpdate = false
        this.container.add(this.startingScreen.loadingLabel.mesh)

        // Start label
        this.startingScreen.startLabel = {}
        this.startingScreen.startLabel.geometry = new THREE.PlaneBufferGeometry(2.5, 2.5 / 4)
        this.startingScreen.startLabel.image = new Image()
        this.startingScreen.startLabel.image.src = 'data:image/png;base64,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'
        this.startingScreen.startLabel.texture = new THREE.Texture(this.startingScreen.startLabel.image)
        this.startingScreen.startLabel.texture.magFilter = THREE.NearestFilter
        this.startingScreen.startLabel.texture.minFilter = THREE.LinearFilter
        this.startingScreen.startLabel.texture.needsUpdate = true
        this.startingScreen.startLabel.material = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, color: 0xffffff, alphaMap: this.startingScreen.startLabel.texture })
        this.startingScreen.startLabel.material.opacity = 0
        this.startingScreen.startLabel.mesh = new THREE.Mesh(this.startingScreen.startLabel.geometry, this.startingScreen.startLabel.material)
        this.startingScreen.startLabel.mesh.matrixAutoUpdate = false
        this.container.add(this.startingScreen.startLabel.mesh)

        // Progress
        this.resources.on('progress', (_progress) => {
            // Update area
            this.startingScreen.area.floorBorder.material.uniforms.uAlpha.value = 1
            this.startingScreen.area.floorBorder.material.uniforms.uLoadProgress.value = _progress
        })

        // Ready
        this.resources.on('ready', () => {
            window.requestAnimationFrame(() => {
                this.startingScreen.area.activate()

                TweenLite.to(this.startingScreen.area.floorBorder.material.uniforms.uAlpha, 0.3, { value: 0.3 })
                TweenLite.to(this.startingScreen.loadingLabel.material, 0.3, { opacity: 0 })
                TweenLite.to(this.startingScreen.startLabel.material, 0.3, { opacity: 1, delay: 0.3 })
            })
        })

        // On interact, reveal
        this.startingScreen.area.on('interact', () => {
            this.startingScreen.area.deactivate()
            TweenLite.to(this.startingScreen.area.floorBorder.material.uniforms.uProgress, 0.3, { value: 0, delay: 0.4 })

            TweenLite.to(this.startingScreen.startLabel.material, 0.3, { opacity: 0, delay: 0.4 })

            this.start()

            window.setTimeout(() => {
                this.reveal.go()
            }, 600)
        })
    }

    setSounds() {
        this.sounds = new Sounds({
            debug: this.debugFolder,
            time: this.time
        })
    }

    setAxes() {
        this.axis = new THREE.AxesHelper()
        this.container.add(this.axis)
    }

    setControls() {
        this.controls = new Controls({
            config: this.config,
            sizes: this.sizes,
            time: this.time,
            camera: this.camera,
            sounds: this.sounds
        })
    }

    setMaterials() {
        this.materials = new Materials({
            resources: this.resources,
            debug: this.debugFolder
        })
    }

    setFloor() {
        this.floor = new Floor({
            debug: this.debugFolder
        })

        this.container.add(this.floor.container)
    }

    setShadows() {
        this.shadows = new Shadows({
            time: this.time,
            debug: this.debugFolder,
            renderer: this.renderer,
            camera: this.camera
        })
        this.container.add(this.shadows.container)
    }

    setPhysics() {
        this.physics = new Physics({
            config: this.config,
            debug: this.debug,
            time: this.time,
            sizes: this.sizes,
            controls: this.controls,
            sounds: this.sounds
        })

        this.container.add(this.physics.models.container)
    }

    setZones() {
        this.zones = new Zones({
            time: this.time,
            physics: this.physics,
            debug: this.debugFolder
        })
        this.container.add(this.zones.container)
    }

    setAreas() {
        this.areas = new Areas({
            config: this.config,
            resources: this.resources,
            debug: this.debug,
            renderer: this.renderer,
            camera: this.camera,
            car: this.car,
            sounds: this.sounds,
            time: this.time
        })

        this.container.add(this.areas.container)
    }

    setTiles() {
        this.tiles = new Tiles({
            resources: this.resources,
            objects: this.objects,
            debug: this.debug
        })
    }

    setWalls() {
        this.walls = new Walls({
            resources: this.resources,
            objects: this.objects
        })
    }

    setObjects() {
        this.objects = new Objects({
            time: this.time,
            resources: this.resources,
            materials: this.materials,
            physics: this.physics,
            shadows: this.shadows,
            sounds: this.sounds,
            debug: this.debugFolder
        })
        this.container.add(this.objects.container)

        // window.requestAnimationFrame(() =>
        // {
        //     this.objects.merge.update()
        // })
    }

    setCar() {
        this.car = new Car({
            time: this.time,
            resources: this.resources,
            objects: this.objects,
            physics: this.physics,
            shadows: this.shadows,
            materials: this.materials,
            controls: this.controls,
            sounds: this.sounds,
            renderer: this.renderer,
            camera: this.camera,
            debug: this.debugFolder,
            config: this.config
        })
        this.container.add(this.car.container)
    }

    setSections() {
        this.sections = {}

        // Generic options
        const options = {
            config: this.config,
            time: this.time,
            resources: this.resources,
            camera: this.camera,
            passes: this.passes,
            objects: this.objects,
            areas: this.areas,
            zones: this.zones,
            walls: this.walls,
            tiles: this.tiles,
            debug: this.debugFolder
        }

        // // Distinction A
        // this.sections.distinctionA = new DistinctionASection({
        //     ...options,
        //     x: 0,
        //     y: - 15
        // })
        // this.container.add(this.sections.distinctionA.container)

        // // Distinction B
        // this.sections.distinctionB = new DistinctionBSection({
        //     ...options,
        //     x: 0,
        //     y: - 15
        // })
        // this.container.add(this.sections.distinctionB.container)

        // // Distinction C
        // this.sections.distinctionC = new DistinctionCSection({
        //     ...options,
        //     x: 0,
        //     y: 0
        // })
        // this.container.add(this.sections.distinctionC.container)

        // // Distinction D
        // this.sections.distinctionD = new DistinctionDSection({
        //     ...options,
        //     x: 0,
        //     y: 0
        // })
        // this.container.add(this.sections.distinctionD.container)

        // Intro
        this.sections.intro = new IntroSection({
            ...options,
            x: 0,
            y: 0
        })
        this.container.add(this.sections.intro.container)

        // Crossroads
        // this.sections.crossroads = new CrossroadsSection({
        //     ...options,
        //     x: 0,
        //     y: - 30
        // })
        // this.container.add(this.sections.crossroads.container)

        // Projects
        this.sections.projects = new ProjectsSection({
            ...options,
            x: 0,
            y: - 25
            // x: 0,
            // y: 0
        })
        this.container.add(this.sections.projects.container)

        // Information
        this.sections.information = new InformationSection({
            ...options,
            x: 1.2,
            y: 3
            // x: 0,
            // y: - 10
        })
        this.container.add(this.sections.information.container)

        // Playground
        this.sections.playground = new PlaygroundSection({
            ...options,
            x: - 40,
            y: - 34
            // x: - 15,
            // y: - 4
        })
        this.container.add(this.sections.playground.container)
    }

    setEasterEggs() {
        this.easterEggs = new EasterEggs({
            resources: this.resources,
            car: this.car,
            walls: this.walls,
            objects: this.objects,
            materials: this.materials,
            areas: this.areas,
            config: this.config,
            physics: this.physics
        })
        this.container.add(this.easterEggs.container)
    }
}